import { useToast } from '@/hooks/use-toast';
import L from 'leaflet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseDraggableMarkerProps {
    position: [number, number];
    onDragEnd?: (lat: number, lng: number) => void;
}

export const useDraggableMarker = ({ position, onDragEnd }: UseDraggableMarkerProps) => {
    const [markerPosition, setMarkerPosition] = useState<[number, number]>(position);
    const [isDragging, setIsDragging] = useState(false);
    const markerRef = useRef<L.Marker>(null);
    const { toast } = useToast();
    const { t } = useTranslation();

    const eventHandlers = useMemo(() => ({
        dragstart() {
            setIsDragging(true);
        },
        dragend() {
            const marker = markerRef.current;
            if (marker) {
                const newPos = marker.getLatLng();
                setMarkerPosition([newPos.lat, newPos.lng]);
                
                if (onDragEnd) {
                    onDragEnd(newPos.lat, newPos.lng);
                }
                
                toast({
                    title: t('properties.location.markerMoved'),
                    description: t('properties.location.coordinatesUpdated'),
                });

                setTimeout(() => {
                    setIsDragging(false);
                }, 300);
            }
        },
    }), [onDragEnd, toast, t]);

    useEffect(() => {
        setMarkerPosition(position);
    }, [position]);

    useEffect(() => {
        const marker = markerRef.current;
        if (marker) {
            const element = marker.getElement();
            if (element) {
                if (isDragging) {
                    element.classList.add('marker-dragging');
                } else {
                    element.classList.remove('marker-dragging');
                }
            }
        }
    }, [isDragging]);

    return {
        markerPosition,
        markerRef,
        eventHandlers
    };
};